import clsx from 'clsx'

import Link from '@/atoms/Link/Link'
import { SampleTextCard } from '@/molecules/BlogCard/SampleCard'
import { INDUSTRY_CATEGORY_DATABASE_ID } from '@/utils/constants/revalidation'
import { getContentCategoryMetadata } from '@/utils/functions/samples'

/* styling */
import styles from './_.module.css'

interface Props {
  post: SamplePostDetails
  similarPosts: SamplePost[]
  contentCategory: WpContentCategoryName
}

function TextSampleDetails({ post, similarPosts, contentCategory }: Props) {
  const contentCategoryMetadata = getContentCategoryMetadata(contentCategory)

  const primaryCategory =
    post.categories.edges.find(category => category.isPrimary)?.node.name ??
    post.categories.edges[0].node.name

  const industry = post.categories.edges.find(
    category => category.node.parentDatabaseId === INDUSTRY_CATEGORY_DATABASE_ID
  )
  const creatorLevel = post.categories.edges.find(category =>
    category.node.name.startsWith('Creator Level: ')
  )

  const postMetadata = [
    {
      icon: '/assets/icons/bag.svg',
      label: 'Industry',
      value: industry?.node.name ?? ''
    },
    {
      icon: '/assets/icons/pen-tool-dark.svg',
      label: 'Creator Level',
      value: creatorLevel?.node.name.split('Creator Level: ')[1] ?? ''
    },
    {
      icon: '/assets/icons/document.svg',
      label: 'Word Count',
      value: post.sampleTextPostMetadata?.wordCount
        ? `${post.sampleTextPostMetadata.wordCount} words`
        : ''
    },
    {
      icon: '/assets/icons/folder-vector.svg',
      label: 'Purpose',
      value: post.sampleTextPostMetadata?.purpose
    }
  ]

  const contentCategoryIcon: Record<WpContentCategoryName, string> = {
    'Content Category: Text': '/assets/icons/TextService.svg',
    'Content Category: Design': '/assets/icons/DesignService.svg',
    'Content Category: Language & Translation':
      '/assets/icons/LanguageService.svg'
  }

  return (
    <div>
      <article className="max-w-3xl  |  mx-auto px-5 sm:px-10 lg:px-5">
        {/* sample header */}
        <header>
          <p className="text-paragraph-3 font-medium text-purple-50  |  mb-4">
            {primaryCategory}
          </p>
          <div className="flex items-start justify-between">
            <h1 className="h2 font-medium">{post.title}</h1>
            <img
              className="hidden md:block  |  ml-8"
              src={contentCategoryIcon[contentCategory]}
              alt={contentCategory}
            />
          </div>
        </header>

        <hr className="max-desktop-width  |  border-gray-80  |  lg:mx-auto my-10" />

        {/* sample metadata */}
        <section className="text-paragraph-3 md:text-paragraph-2 text-gray-70">
          {post.sampleTextPostMetadata?.projectSummary && (
            <div className="mb-10">
              <h2 className="font-medium  |  mb-2">Project Summary</h2>
              <div
                className="text-gray-70 mb-12"
                dangerouslySetInnerHTML={{
                  __html: post.sampleTextPostMetadata.projectSummary
                }}
              />
            </div>
          )}
          {post.sampleTextPostMetadata?.creatorProfile && (
            <div className="mb-10">
              <h2 className="font-medium  |  mb-2">Creator Profile</h2>
              <div
                className="text-gray-70 mb-12"
                dangerouslySetInnerHTML={{
                  __html: post.sampleTextPostMetadata.creatorProfile
                }}
              />
            </div>
          )}
          <div className="grid gap-8 md:grid-cols-2  |  mb-10">
            {postMetadata.map(metadata => {
              if (metadata.value)
                return (
                  <div key={metadata.value} className="flex items-center">
                    <img
                      src={metadata.icon}
                      alt={metadata.label}
                      className="mr-6"
                    />
                    <div>
                      <span className="block  |  text-gray-50  text-caption tracking-wider uppercase font-medium">
                        {metadata.label}
                      </span>
                      <span className="text-paragraph-3 font-medium">
                        {metadata.value}
                      </span>
                    </div>
                  </div>
                )
            })}
          </div>
        </section>

        <hr className="max-desktop-width  |  border-gray-80  |  lg:mx-auto my-10" />

        {/* sample body */}
        <section
          className={clsx(
            styles.blogContent,
            'text-paragraph-3 md:text-paragraph-2 text-gray-70  |  mb-16'
          )}
          dangerouslySetInnerHTML={{ __html: post.content }}
        />
      </article>

      <section className="max-desktop-width px-5 sm:px-10 lg:px-6 mx-auto |   border-t border-gray-90  | py-16">
        <h3 className="h4 font-medium text-center  |  mb-10">
          More in {contentCategoryMetadata.title}
        </h3>
        <div className="grid gap-4 md:gap-8 grid-cols-1 md:grid-cols-2 lg:grid-cols-4  |  mb-10">
          {similarPosts
            .filter(_post => _post.slug !== post.slug)
            .splice(0, 8)
            .map(_post => {
              return (
                <article key={_post.id}>
                  <SampleTextCard
                    title={_post.title}
                    slug={`/samples/${_post.slug}/`}
                    categories={_post.categories.edges}
                    wordCount={
                      _post.sampleTextPostMetadata?.wordCount ?? undefined
                    }
                  />
                </article>
              )
            })}
        </div>

        <div className="flex items-center justify-center">
          <Link
            href={contentCategoryMetadata.slug}
            className="flex items-center justify-center  |  text-paragraph-4 font-medium bg-blue-50 text-white  |  rounded-full  |  h-8  |  px-6"
          >
            View All Samples
          </Link>
        </div>
      </section>
    </div>
  )
}

export default TextSampleDetails
